import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  LogOut, KeyRound, Loader2, Package, Bell, Mail, Inbox, X, ExternalLink,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["new", "reviewing", "accepted", "declined", "sold", "paid"];
const STATUS_COLORS = {
  new: "bg-[#3FE1D1] text-[#050505]",
  reviewing: "bg-yellow-400/20 text-yellow-300 border border-yellow-400/40",
  accepted: "bg-emerald-400/20 text-emerald-300 border border-emerald-400/40",
  declined: "bg-red-400/20 text-red-300 border border-red-400/40",
  sold: "bg-blue-400/20 text-blue-300 border border-blue-400/40",
  paid: "bg-white/15 text-white border border-white/30",
};

export default function AdminInbox() {
  const navigate = useNavigate();
  const token = typeof window !== "undefined" ? localStorage.getItem("mp_admin_token") : null;
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("all");
  const [pwOpen, setPwOpen] = useState(false);

  const logout = useCallback(() => {
    localStorage.removeItem("mp_admin_token");
    localStorage.removeItem("mp_admin_name");
    navigate("/admin/login");
  }, [navigate]);

  const loadAll = useCallback(async () => {
    if (!token) {
      navigate("/admin/login");
      return;
    }
    setLoading(true);
    try {
      const [c, s] = await Promise.all([
        axios.get(`${API}/admin/consignments`, authHeaders),
        axios.get(`${API}/admin/stats`, authHeaders),
      ]);
      setItems(c.data || []);
      setStats(s.data);
    } catch (err) {
      if (err.response?.status === 401) {
        logout();
      } else {
        toast.error("Failed to load submissions.");
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, navigate, logout]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/consignments/${id}/status`, { status }, authHeaders);
      setItems((prev) => prev.map((it) => (it.id === id ? { ...it, status } : it)));
      toast.success(`Marked as ${status}`);
    } catch {
      toast.error("Could not update status.");
    }
  };

  const filtered = filter === "all" ? items : items.filter((i) => i.status === filter);

  const STAT_CARDS = stats
    ? [
        { label: "Total consignments", value: stats.consignments, icon: Package },
        { label: "New / unreviewed", value: stats.new_consignments, icon: Inbox },
        { label: "Show reminders", value: stats.reminders, icon: Bell },
        { label: "Newsletter", value: stats.newsletter, icon: Mail },
      ]
    : [];

  return (
    <main data-testid="admin-inbox" className="min-h-screen bg-[#050505] text-white">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-[#050505]/85 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/brand/mp-wordmark-white.png" alt="Monthly Pops" className="h-5" />
            <span className="hidden sm:inline text-white/30">/</span>
            <span className="hidden sm:inline text-xs uppercase tracking-[0.2em] text-white/60">Inbox</span>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-change-password"
              onClick={() => setPwOpen(true)}
              className="inline-flex items-center gap-2 text-white/70 hover:text-[#3FE1D1] text-xs uppercase tracking-wide transition-colors"
            >
              <KeyRound size={15} /> <span className="hidden sm:inline">Password</span>
            </button>
            <button
              data-testid="admin-logout"
              onClick={logout}
              className="inline-flex items-center gap-2 border border-white/15 px-4 py-2 text-white/80 hover:border-[#3FE1D1] hover:text-[#3FE1D1] text-xs uppercase tracking-wide transition-colors"
            >
              <LogOut size={15} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
        <h1 className="font-display font-extrabold uppercase tracking-tighter text-3xl md:text-5xl leading-none">
          Consignment <span className="text-[#3FE1D1]">Inbox</span>
        </h1>

        {/* Stats */}
        <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {STAT_CARDS.map((s) => (
            <div key={s.label} className="bg-[#131315] border border-[#262629] p-5">
              <s.icon size={18} className="text-[#3FE1D1]" />
              <div className="mt-3 font-impact text-4xl leading-none">{s.value}</div>
              <div className="mt-1 text-white/50 text-xs uppercase tracking-wide">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="mt-10 flex gap-2 overflow-x-auto no-scrollbar">
          {["all", ...STATUSES].map((s) => (
            <button
              key={s}
              data-testid={`admin-filter-${s}`}
              onClick={() => setFilter(s)}
              className={`whitespace-nowrap px-4 py-2 text-xs uppercase tracking-wide border transition-colors ${
                filter === s
                  ? "bg-white text-[#050505] border-white font-bold"
                  : "border-[#262629] text-white/60 hover:border-white/40"
              }`}
            >
              {s}
            </button>
          ))}
        </div>

        {/* List */}
        <div className="mt-8 space-y-5">
          {loading ? (
            <div className="py-24 flex justify-center text-white/40">
              <Loader2 className="animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-24 text-center text-white/40">No submissions{filter !== "all" ? ` with status "${filter}"` : ""} yet.</div>
          ) : (
            filtered.map((c) => (
              <div
                key={c.id}
                data-testid={`admin-consignment-${c.id}`}
                className="bg-[#131315] border border-[#262629] p-6 md:p-7"
              >
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-display font-bold text-xl">{c.name}</h3>
                      <span className={`text-[10px] uppercase tracking-[0.16em] font-bold px-2.5 py-1 ${STATUS_COLORS[c.status] || "bg-white/10"}`}>
                        {c.status}
                      </span>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-white/50">
                      <a href={`mailto:${c.email}`} className="hover:text-[#3FE1D1]">{c.email}</a>
                      {c.phone && <span>{c.phone}</span>}
                      <span>{new Date(c.created_at).toLocaleString()}</span>
                    </div>
                  </div>
                  <select
                    data-testid={`admin-status-${c.id}`}
                    value={c.status}
                    onChange={(e) => setStatus(c.id, e.target.value)}
                    className="bg-[#050505] border border-[#262629] text-white px-4 py-2.5 text-sm focus:outline-none focus:border-[#3FE1D1] appearance-none"
                  >
                    {STATUSES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>

                <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-white/40 text-xs uppercase tracking-wide mb-1">Category</p>
                    <p className="text-white/80">{c.category}</p>
                  </div>
                  <div>
                    <p className="text-white/40 text-xs uppercase tracking-wide mb-1">Est. value</p>
                    <p className="text-white/80">{c.estimated_value || "—"}</p>
                  </div>
                  <div>
                    <p className="text-white/40 text-xs uppercase tracking-wide mb-1">Preferred channel</p>
                    <p className="text-white/80">{c.preferred_channel || "—"}</p>
                  </div>
                </div>

                <div className="mt-4">
                  <p className="text-white/40 text-xs uppercase tracking-wide mb-1">Item description</p>
                  <p className="text-white/75 leading-relaxed">{c.item_description}</p>
                  {c.message && <p className="mt-2 text-white/50 italic">"{c.message}"</p>}
                </div>

                {c.photos && c.photos.length > 0 && (
                  <div className="mt-5">
                    <p className="text-white/40 text-xs uppercase tracking-wide mb-2">
                      Photos ({c.photos.length})
                    </p>
                    <div className="flex flex-wrap gap-3">
                      {c.photos.map((p, i) => (
                        <a
                          key={i}
                          href={`${API}/files/${p.storage_path}`}
                          target="_blank"
                          rel="noreferrer"
                          data-testid={`admin-photo-${c.id}-${i}`}
                          className="group relative h-24 w-24 border border-[#262629] overflow-hidden hover:border-[#3FE1D1] transition-colors"
                        >
                          <img
                            src={`${API}/files/${p.storage_path}`}
                            alt={p.original_filename}
                            className="w-full h-full object-cover"
                          />
                          <span className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                            <ExternalLink size={16} className="text-[#3FE1D1]" />
                          </span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {pwOpen && <ChangePasswordModal onClose={() => setPwOpen(false)} authHeaders={authHeaders} />}
    </main>
  );
}

function ChangePasswordModal({ onClose, authHeaders }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [loading, setLoading] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/admin/change-password`, { current_password: current, new_password: next }, authHeaders);
      toast.success("Password updated.");
      onClose();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not change password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={onClose}>
      <div
        data-testid="admin-password-modal"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-sm bg-[#131315] border border-[#262629] p-8 relative"
      >
        <button onClick={onClose} className="absolute top-4 right-4 text-white/50 hover:text-white">
          <X size={18} />
        </button>
        <h3 className="font-display font-bold text-xl mb-6">Change password</h3>
        <form onSubmit={save} className="space-y-4">
          <input
            data-testid="admin-current-password"
            type="password"
            placeholder="Current password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            className="w-full bg-[#050505] border border-[#262629] text-white placeholder:text-white/30 px-4 py-3 text-sm focus:outline-none focus:border-[#3FE1D1]"
          />
          <input
            data-testid="admin-new-password"
            type="password"
            placeholder="New password (min 8 chars)"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            className="w-full bg-[#050505] border border-[#262629] text-white placeholder:text-white/30 px-4 py-3 text-sm focus:outline-none focus:border-[#3FE1D1]"
          />
          <button
            data-testid="admin-save-password"
            disabled={loading}
            className="w-full bg-[#3FE1D1] text-[#050505] font-bold uppercase text-sm tracking-widest px-6 py-3.5 hover:bg-white transition-colors disabled:opacity-60"
          >
            {loading ? "Saving…" : "Save"}
          </button>
        </form>
      </div>
    </div>
  );
}
